import React from "react";

import { mdiViewGridOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { SmartlistType } from "@/src/api/MediaAPI.types";

import ProjectAPI from "../../../../api/ProjectAPI";
import SmartlistAPI from "../../../../api/SmartlistAPI";
import { BUTTON_VARIANTS } from "../../../../components/button/Button";
import Input from "../../../../components/form/Input";
import Select from "../../../../components/form/Select";
import Modal from "../../../../components/modal/Modal";

import type { JSX } from "react";

import type { CategoriesComponent } from "../../../../api/ProjectAPI.types";
import type { ModalActions } from "../../../../components/modal/Modal";
import type { AppState } from "../../../../reducers";

function ModalHeader(): JSX.Element {
  const { t } = useTranslation("editor-platform");

  return (
    <div className="flex flex-wrap items-center gap-[0.9375rem]">
      <div className="border-border flex rounded-[0.46875rem] border p-[0.46875rem]">
        <Icon path={mdiViewGridOutline} size={1.25} />
      </div>
      <div className="shrink-0 grow basis-0 text-[1.171875rem] font-semibold">
        {t("categoriesComponentSettingsModalTitle")}
      </div>
    </div>
  );
}

interface FormValues {
  smartlist?: { label?: string; value: string } | null;
  show_my_medias: boolean;
  show_my_playlists: boolean;
}

interface CategoriesComponentSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  component?: CategoriesComponent;
}

function CategoriesComponentSettingsModal({
  isOpen,
  onClose,
  component,
}: CategoriesComponentSettingsModalProps): JSX.Element | null {
  const { projectSlug } = useParams<{
    projectSlug: string;
  }>();
  const { t } = useTranslation("editor-platform");

  const playlistsType = useSelector(
    (state: AppState) =>
      state.architectureReducer?.project?.storefront_project_params
        ?.playlists_type,
  );

  const isSinglePlaylistProject = playlistsType === "single";
  const isMultiplePlaylistsProject = playlistsType === "multiple";

  const getSmartlistListQuery = SmartlistAPI.generateQuery({
    fn: SmartlistAPI.getSmartlistList,
    params: [
      {
        projectSlug,
      },
    ],
  });

  const { data: smartlists, isLoading } = useQuery(getSmartlistListQuery);

  const updateComponentMutation = useMutation({
    mutationFn: async (updatedComponent: CategoriesComponent) =>
      ProjectAPI.updateComponent({ component: updatedComponent, projectSlug }),
    onSuccess: () => {
      toast.success(t("categoriesComponentUpdated"));
      onClose();
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const methods = useForm<FormValues>({
    values: {
      smartlist: component?.params.smartlist_uuid
        ? { value: component.params.smartlist_uuid }
        : null,
      show_my_medias: component?.params.show_my_medias ?? true,
      show_my_playlists: component?.params.show_my_playlists ?? true,
    },
  });

  if (!component) {
    return null;
  }

  const handleSubmit = (data: FormValues): void => {
    updateComponentMutation.mutate({
      ...component,
      params: {
        ...component.params,
        smartlist_uuid: data.smartlist?.value ?? "",
        show_my_medias: data.show_my_medias,
        show_my_playlists: data.show_my_playlists,
      },
    });
  };

  const smartlistOptions = smartlists?.data
    .filter(
      (smartlist) =>
        smartlist.type === SmartlistType.STANDARD ||
        smartlist.type === SmartlistType.SERIES,
    )
    .map((smartlist) => ({
      label: smartlist.label,
      value: smartlist.uuid,
    }));

  const formId = "categoriesComponentSettingsForm";

  const modalActions: ModalActions[] = [
    {
      label: t("confirm"),
      buttonVariant: BUTTON_VARIANTS.PRIMARY,
      isFullWidth: true,
      isCloseAfterAction: false,
      formId,
    },
  ];

  return (
    <Modal
      isDivided
      actions={modalActions}
      header={<ModalHeader />}
      isOpen={isOpen}
      onClose={onClose}
      size="md"
    >
      <FormProvider
        {...methods} // eslint-disable-line react/jsx-props-no-spreading
      >
        <form id={formId} onSubmit={methods.handleSubmit(handleSubmit)}>
          <Select
            isPortal
            isRequired
            isLoading={isLoading}
            label={t("smartlist")}
            name="smartlist"
            options={smartlistOptions}
          />
          {isSinglePlaylistProject ? (
            <Input
              label={t("show_my_medias")}
              name="show_my_medias"
              size="sm"
              type="switch"
            />
          ) : null}
          {isMultiplePlaylistsProject ? (
            <Input
              label={t("show_my_playlists")}
              name="show_my_playlists"
              size="sm"
              type="switch"
            />
          ) : null}
        </form>
      </FormProvider>
    </Modal>
  );
}

export default CategoriesComponentSettingsModal;
